// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { DSNSnapshot } from '../types';
import {
  activeDishCount,
  extractPairs,
  highestRTLT,
  totalDownlinkRate,
} from '../state';
import type { SessionState } from '../state';
import { formatDataRate, formatRTLT, formatNumber, formatRelative, formatClockUTC } from '../utils/format';
import { getSpacecraftInfo } from '../data/spacecraft';

export function renderInsights(snapshot: DSNSnapshot | null, session: SessionState): string {
  if (!snapshot) return `<p style="color:var(--text-tertiary)">Loading…</p>`;

  const pairs = extractPairs(snapshot).filter(
    (p) => p.spacecraft && p.spacecraft !== 'DSN' && p.spacecraft !== 'DSS',
  );
  const totalDishes = snapshot.complexes.flatMap((c) => c.dishes).length;
  const active = activeDishCount(snapshot);
  const uniqSc = new Set(pairs.map((p) => p.spacecraft));
  const totalDown = totalDownlinkRate(snapshot);
  const top = highestRTLT(snapshot);

  const highestRate = pairs.reduce(
    (acc, p) => {
      const max = Math.max(0, ...p.dataRates);
      if (max > acc.rate) return { rate: max, name: p.spacecraft, dish: p.dish };
      return acc;
    },
    { rate: 0, name: '—', dish: '—' },
  );

  let topMission = '—';
  if (top) {
    const info = getSpacecraftInfo(top.name);
    topMission = `${info.name} · ${formatRTLT(top.rtlt * 2)}`;
  }

  const stats = `
    <div class="insights-grid">
      <div class="stat-card">
        <div class="stat-label">Antennas on sky</div>
        <div class="stat-value">${active} / ${totalDishes}</div>
        <div class="stat-sub">Actively tracking a spacecraft</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Spacecraft reached</div>
        <div class="stat-value">${uniqSc.size}</div>
        <div class="stat-sub">Unique missions on the network right now</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Combined downlink</div>
        <div class="stat-value">${formatDataRate(totalDown) === '—' ? '—' : formatDataRate(totalDown)}</div>
        <div class="stat-sub">Sum of active down-signal rates</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Fastest single link</div>
        <div class="stat-value">${formatDataRate(highestRate.rate)}</div>
        <div class="stat-sub">${highestRate.name === '—' ? '—' : `${getSpacecraftInfo(highestRate.name).name} via ${highestRate.dish}`}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Furthest round-trip</div>
        <div class="stat-value">${top ? formatRTLT(top.rtlt * 2).split(' ')[0] : '—'}</div>
        <div class="stat-sub">${topMission}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Session discoveries</div>
        <div class="stat-value">${session.spacecraftSeen.size}</div>
        <div class="stat-sub">Unique spacecraft seen since you opened the tab</div>
      </div>
    </div>
  `;

  const insights: string[] = [];
  if (active === 0) {
    insights.push(insightCard('warn', 'All antennas currently idle', 'Every DSN dish reports engineering or maintenance activity — unusual for more than a few minutes at a time. Refresh in a few seconds.'));
  }
  if (top && top.rtlt > 30000) {
    insights.push(insightCard('good', 'Voyager-class signal in play', `The longest round-trip in flight right now is ${formatRTLT(top.rtlt * 2)} — this signal has been travelling since before you sat down.`));
  }
  if (highestRate.rate > 10_000_000) {
    insights.push(insightCard('info', `${getSpacecraftInfo(highestRate.name).name} is firehosing data`, `A single antenna (${highestRate.dish}) is pulling ${formatDataRate(highestRate.rate)} off the sky — typically a near-Earth science mission dumping imagery.`));
  }
  const mspaDishes = snapshot.complexes.flatMap((c) => c.dishes).filter((d) => d.isMSPA);
  if (mspaDishes.length > 0) {
    insights.push(insightCard('info', `${mspaDishes.length} antenna${mspaDishes.length > 1 ? 's' : ''} running MSPA`, 'Multiple Spacecraft Per Antenna — one dish is receiving from several spacecraft that happen to be angularly close on the sky. A clever bandwidth saver.'));
  }
  if (insights.length === 0) {
    insights.push(insightCard('info', 'Network operating normally', `${active} of ${totalDishes} dishes tracking ${uniqSc.size} spacecraft. Data flowing to Earth at a combined ${formatDataRate(totalDown)}.`));
  }

  return `
    <div class="view-header">
      <h2 class="view-title">Insights</h2>
      <p class="view-subtitle">
        At-a-glance numbers for the whole network, plus automatic callouts for anything unusual right now.
        Session stats accumulate while this tab stays open.
      </p>
    </div>
    ${stats}
    <h3 style="font-size: var(--font-size-lg); margin: var(--space-xl) 0 var(--space-sm); color: var(--text-primary);">Right now</h3>
    ${insights.join('')}
    ${renderHistoryTimeline(session)}
  `;
}

function insightCard(type: 'good' | 'warn' | 'bad' | 'info', title: string, desc: string): string {
  return `
    <div class="insight-card ${type}">
      <div class="insight-title">${title}</div>
      <div class="insight-desc">${desc}</div>
    </div>
  `;
}

function renderHistoryTimeline(session: SessionState): string {
  if (session.history.length === 0) {
    return `
      <div class="history-timeline">
        <h3 style="font-size: var(--font-size-lg); margin-bottom: var(--space-sm);">Session history</h3>
        <div class="history-empty">The browser hasn't seen any tracks yet — stay on the tab for a minute and this timeline will fill in.</div>
      </div>
    `;
  }

  const earliest = session.firstSeen;
  const latest = Date.now();
  const span = Math.max(60_000, latest - earliest);

  const spacecraftList = [...session.spacecraftSeen].sort();
  const rows = spacecraftList
    .map((sc) => {
      const events = session.history.filter((e) => e.spacecraft === sc);
      const info = getSpacecraftInfo(sc);
      const segments = events
        .map((e) => {
          const pct = ((e.ts - earliest) / span) * 100;
          const segTip = `${info.name} · ${e.dish} · ${formatClockUTC(new Date(e.ts))}`;
          return `<div class="history-seg" style="left: ${Math.max(0, Math.min(99, pct))}%; width: 3px;" data-tip="${segTip}" aria-label="${segTip}"></div>`;
        })
        .join('');
      const rowTip = `${info.name} — ${events.length} tracking event${events.length === 1 ? '' : 's'} this session`;
      return `
        <div class="history-row">
          <div class="history-label" data-tip="${rowTip}" aria-label="${rowTip}">${info.name}</div>
          <div class="history-bar">${segments}</div>
        </div>
      `;
    })
    .join('');

  return `
    <div class="history-timeline">
      <h3 style="font-size: var(--font-size-lg); margin-bottom: var(--space-xs);">Session history</h3>
      <p style="font-size: var(--font-size-sm); color: var(--text-tertiary); margin-bottom: var(--space-md);">
        Spacecraft observed since you opened this tab (${formatRelative(Date.now() - session.firstSeen)}).
        Each tick = a tracking event seen in the DSN feed. ${formatNumber(session.history.length)} events logged.
      </p>
      ${rows}
    </div>
  `;
}
